<template>
    <div class="background-small">
        <div class="header-section">
            <div class="header-left">
                <button class="back-btn" @click="$router.push('/support')">
                    <i class="fas fa-arrow-left"></i>
                </button>
                <div class="ticket-header-info">
                    <h4 class="text-white mb-0">Ticket #{{ ticket?.ticket_number }}</h4>
                    <div class="ticket-status">
                        <span class="status-badge" :class="ticket?.status.toLowerCase()">
                            {{ formatStatus(ticket?.status) }}
                        </span>
                        <span class="priority-badge" :class="ticket?.priority.toLowerCase()">
                            {{ ticket?.priority }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="ticket-container" v-if="ticket">
            <div class="ticket-info-section">
                <div class="ticket-main-info">
                    <h5 class="ticket-title">{{ ticket.title }}</h5>
                    <span class="ticket-type" :class="ticket.type.toLowerCase()">
                        {{ formatType(ticket.type) }}
                    </span>
                </div>
                <p class="ticket-description">{{ ticket.description }}</p>
                <div class="ticket-meta">
                    <span class="meta-item">
                        <i class="fas fa-clock"></i>
                        Created: {{ formatDate(ticket.created_at) }}
                    </span>
                    <span class="meta-item" v-if="ticket.resolved_at">
                        <i class="fas fa-check-circle"></i>
                        Resolved: {{ formatDate(ticket.resolved_at) }}
                    </span>
                    <span class="meta-item" v-if="ticket.closed_at">
                        <i class="fas fa-times-circle"></i>
                        Closed: {{ formatDate(ticket.closed_at) }}
                    </span>
                </div>
            </div>

            <div class="messages-section">
                <div class="messages-container" ref="messagesContainer">
                    <div v-for="message in messages" :key="message.id" class="message"
                        :class="{ 'admin-message': message.author?.ROLE === USER_ROLES.ADMIN, 'user-message': message.author?.ROLE !== USER_ROLES.ADMIN }">
                        <div class="message-header">
                            <div class="message-user-info">
                                <span class="user-name">{{ message.author?.user_name || 'User' }}</span>
                                <span class="user-role" :class="{ 'admin': message.author?.ROLE === USER_ROLES.ADMIN }">
                                    {{ message.author?.ROLE === USER_ROLES.ADMIN ? 'Admin' : 'User' }}
                                </span>
                                <span class="message-time">{{ formatDate(message.created_at) }}</span>
                            </div>
                        </div>
                        <div class="message-content">{{ message.content }}</div>
                    </div>
                </div>

                <div class="reply-box" v-if="ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED'">
                    <textarea v-model="replyText" placeholder="Type your reply here..." rows="3"
                        @keydown.enter.exact.prevent="sendReply" @keydown.enter.shift.exact="newLine"></textarea>
                    <div class="reply-actions">
                        <span class="reply-hint">Press Enter to send, Shift + Enter for new line</span>
                        <button class="reply-btn" @click="sendReply" :disabled="!replyText.trim()">
                            <i class="fas fa-paper-plane"></i>
                            Send Reply
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="loading-screen">
            <DotLoader :width="'50px'" />
        </div>

        <InfoModal v-if="infoModal.show" :duration="infoModal.duration" :title="infoModal.title"
            :message="infoModal.message" :type="infoModal.type" @close="infoModal.show = false" />
    </div>
</template>

<script>
import DateUtils from "@/utils/DateUtils.js";
import SupportService from "@/services/SupportService.js";
import InfoModal from "@/components/modal/InfoModal.vue";
import { USER_ROLES } from "@/core/constants.js";

const TICKET_TYPES = {
    GENERAL: 'General Support',
    PAYMENT: 'Payment Issue',
    TECHNICAL: 'Technical Support',
    BILLING: 'Billing Issue',
    SUPPORT: 'Customer Support',
    COMPLAINT: 'Complaint',
    FEATURE_REQUEST: 'Feature Request',
    BUG_REPORT: 'Bug Report'
};

const TICKET_STATUSES = {
    OPEN: 'Open',
    IN_PROGRESS: 'In Progress',
    WAITING_USER_RESPONSE: 'Waiting for Response',
    PENDING: 'Pending',
    RESOLVED: 'Resolved',
    CLOSED: 'Closed',
    CANCELLED: 'Cancelled'
};

export default {
    name: 'TicketDetail',
    components: {
        InfoModal
    },
    data() {
        return {
            ticket: null,
            messages: [],
            replyText: '',
            currentUserId: null,
            USER_ROLES,
            infoModal: {
                show: false,
                title: '',
                message: '',
                type: 'info',
                duration: "4s",
                durationSeconds: 4000
            }
        }
    },
    methods: {
        formatType(type) {
            return TICKET_TYPES[type] || type;
        },
        formatStatus(status) {
            return TICKET_STATUSES[status] || status;
        },
        formatDate(date) {
            return DateUtils.getDate(date);
        },
        async showInfo(title, message, type = 'info') {
            this.infoModal = {
                show: true,
                title,
                message,
                type,
                duration: "4s",
                durationSeconds: 4000
            };
            this.infoModal.show = true;
            setTimeout(() => {
                this.infoModal.show = false;
            }, this.infoModal.durationSeconds);
        },
        async loadTicket() {
            const response = await SupportService.getTicketById(this.$route.params.id);
            if (!response) return await this.showInfo('Error', 'Failed to load ticket details', 'error');

            this.ticket = response;
            await this.loadMessages();
        },
        async loadMessages() {
            const messages = await SupportService.getReplies(this.ticket.id);
            if (!messages) return await this.showInfo('Error', 'Failed to load messages', 'error');

            this.messages = messages.messages;
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },
        async sendReply() {
            if (!this.replyText.trim()) return;

            const response = await SupportService.addReply({
                ticketId: this.ticket.id,
                content: this.replyText
            });

            if (!response) return await this.showInfo('Error', 'Failed to send reply', 'error');

            await this.showInfo('Success', 'Reply sent successfully', 'info');
            await this.loadMessages();
            this.replyText = '';
        },
        newLine(e) {
            const textarea = e.target;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            this.replyText = this.replyText.substring(0, start) + '\\n' + this.replyText.substring(end);
            this.$nextTick(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 1;
            });
        },
        scrollToBottom() {
            if (this.$refs.messagesContainer) {
                this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
            }
        }
    },
    async created() {
        await this.loadTicket();
    },
    watch: {
        '$route.params.id': {
            handler: 'loadTicket',
            immediate: true
        }
    }
}
</script>

<style scoped>
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 0 1rem;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.back-btn {
    background: none;
    border: none;
    color: white;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    transition: all 0.2s ease;
}

.back-btn:hover {
    background: rgba(255, 255, 255, 0.1);
}

.ticket-header-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.ticket-status {
    display: flex;
    gap: 0.5rem;
}

.close-ticket-btn {
    background: #ef4444;
    border: none;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.close-ticket-btn:hover {
    background: #dc2626;
}

.ticket-container {
    background: var(--secondary-color);
    border-radius: 12px;
    overflow: hidden;
    margin: 0 1rem;
}

.ticket-info-section {
    padding: 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.ticket-main-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
}

.ticket-title {
    color: white;
    margin: 0;
    font-size: 1.25rem;
}

.ticket-type {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    text-transform: uppercase;
}

.ticket-description {
    color: #ccc;
    font-size: 0.9rem;
    line-height: 1.6;
    margin-bottom: 1rem;
    white-space: pre-wrap;
}

.ticket-meta {
    display: flex;
    gap: 1.5rem;
    color: #666;
    font-size: 0.8rem;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.messages-section {
    padding: 1.5rem;
}

.messages-container {
    max-height: calc(100vh - 400px);
    min-height: 300px;
    overflow-y: auto;
    margin-bottom: 1.5rem;
    padding-right: 0.5rem;
}

.message {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 1.2rem;
    margin-bottom: 1rem;
    max-width: 80%;
    position: relative;
    transition: all 0.2s ease;
}

.message.user-message {
    background: rgba(149, 76, 233, 0.1);
    margin-left: auto;
    border-bottom-right-radius: 4px;
}

.message.admin-message {
    background: rgba(59, 130, 246, 0.1);
    margin-right: auto;
    border-bottom-left-radius: 4px;
}

.message-header {
    margin-bottom: 0.75rem;
}

.message-user-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.user-name {
    color: white;
    font-weight: 500;
    font-size: 0.9rem;
}

.user-role {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.1);
    color: #ccc;
    text-transform: uppercase;
}

.user-role.admin {
    background: rgba(59, 130, 246, 0.2);
    color: #3b82f6;
}

.message-time {
    color: #666;
    font-size: 0.8rem;
}

.message-content {
    color: #ccc;
    font-size: 0.9rem;
    line-height: 1.6;
    white-space: pre-wrap;
}

.admin-message .message-content {
    color: #e5e7eb;
}

.user-message .message-content {
    color: #e5e7eb;
}

.reply-box {
    margin-top: 1.5rem;
    position: sticky;
    bottom: 0;
    background: var(--secondary-color);
    padding: 1rem 0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.reply-box textarea {
    width: 100%;
    padding: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: white;
    resize: vertical;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    max-height: 150px;
}

.reply-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
}

.reply-hint {
    color: #666;
    font-size: 0.8rem;
}

.reply-btn {
    background: #10b981;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.reply-btn:hover {
    background: #059669;
}

.reply-btn:disabled {
    background: #666;
    cursor: not-allowed;
}

.loading-state {
    text-align: center;
    padding: 3rem;
    color: #666;
    font-size: 1.1rem;
}

.loading-state i {
    font-size: 2rem;
    margin-bottom: 1rem;
}

@media (max-width: 768px) {
    .header-section {
        padding: 0;
    }

    .ticket-header-info h4 {
        font-size: 1.1rem;
    }

    .ticket-status {
        flex-wrap: wrap;
    }

    .status-badge,
    .priority-badge {
        font-size: 0.7rem;
    }

    .close-ticket-btn {
        padding: 0.5rem 1rem;
        font-size: 0.9rem;
    }

    .ticket-container {
        margin: 0;
        border-radius: 0;
    }

    .ticket-info-section {
        padding: 1rem;
    }

    .ticket-title {
        font-size: 1.1rem;
    }

    .ticket-type {
        font-size: 0.7rem;
    }

    .ticket-description {
        font-size: 0.85rem;
    }

    .ticket-meta {
        flex-wrap: wrap;
        gap: 1rem;
    }

    .messages-section {
        padding: 1rem;
    }

    .messages-container {
        padding-right: 0;
        max-height: calc(100vh - 350px);
        min-height: 250px;
    }

    .message {
        max-width: 90%;
        padding: 1rem;
    }

    .message-user-info {
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .user-name {
        font-size: 0.85rem;
    }

    .user-role {
        font-size: 0.65rem;
        padding: 0.15rem 0.3rem;
    }

    .message-time {
        font-size: 0.75rem;
    }

    .message-content {
        font-size: 0.85rem;
    }

    .reply-box {
        margin-top: 1rem;
        padding: 0.75rem 0;
    }

    .reply-box textarea {
        padding: 0.75rem;
        font-size: 0.85rem;
        max-height: 100px;
    }

    .reply-actions {
        flex-direction: column-reverse;
        align-items: stretch;
        gap: 0.5rem;
    }

    .reply-hint {
        text-align: center;
        font-size: 0.75rem;
    }

    .reply-btn {
        width: 100%;
        padding: 0.75rem;
        justify-content: center;
    }
}
</style>